/**
 * PrimaryFaultCard Component
 * Large card showing dominant fault with severity indicator and consistency score
 */

import { GolfCard, GolfCardContent, GolfCardHeader, GolfCardTitle } from '@/components/golf/golf-card';
import { AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PrimaryFaultCardProps {
  taxonomyKey: string;
  displayName?: string;
  avgSeverity: number;
  consistency: number; // 0-1 (ratio) or 0-5 (count) - handles both formats
  className?: string;
}

export function PrimaryFaultCard({
  taxonomyKey,
  displayName,
  avgSeverity,
  consistency,
  className
}: PrimaryFaultCardProps) {
  const severityLabels = ['Good', 'Minor', 'Moderate', 'Major'];
  const severityColors = [
    'bg-success/20 text-success',
    'bg-info/20 text-info',
    'bg-warning/20 text-warning',
    'bg-error/20 text-error'
  ];

  const severityIndex = Math.round(avgSeverity);
  const severityLabel = severityLabels[severityIndex] || 'Unknown';
  const severityColor = severityColors[severityIndex] || severityColors[0];

  // Handle both ratio (0-1) and count (0-5) formats
  // If consistency <= 1, it's a ratio; otherwise it's a count out of 5
  const isRatio = consistency <= 1;
  const consistencyPercentage = isRatio ? consistency * 100 : (consistency / 5) * 100;
  const consistencyCount = isRatio ? Math.round(consistency * 5) : consistency;

  return (
    <GolfCard className={cn("border-2 border-primary/20", className)}>
      <GolfCardHeader>
        <GolfCardTitle className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-warning" />
          Primary Fault
        </GolfCardTitle>
      </GolfCardHeader>
      <GolfCardContent className="space-y-4">
        {/* Fault Name */}
        <div>
          <h3 className="text-xl font-semibold text-foreground mb-1">
            {displayName || taxonomyKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
          </h3>
          <p className="text-sm text-muted-foreground">
            Taxonomy ID: {taxonomyKey}
          </p>
        </div>

        {/* Severity Indicator */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Average Severity</span>
            <span className={cn("px-3 py-1 rounded-full text-sm font-semibold", severityColor)}>
              {severityLabel} ({avgSeverity.toFixed(1)}/3.0)
            </span>
          </div>
          <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
            <div
              className={cn("h-full transition-all", severityColor.split(' ')[0])}
              style={{ width: `${(avgSeverity / 3) * 100}%` }}
            />
          </div>
        </div>

        {/* Consistency Score */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Consistency</span>
            <span className="text-sm font-semibold text-foreground">
              {consistencyCount} of 5 swings ({consistencyPercentage.toFixed(0)}%)
            </span>
          </div>
          <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary transition-all"
              style={{ width: `${consistencyPercentage}%` }}
            />
          </div>
        </div>
      </GolfCardContent>
    </GolfCard>
  );
}


